/**
 * Availability Indicator Utility
 *
 * Provides visual indicators for slot availability based on capacity thresholds.
 * Used across web and mobile apps for consistent UX in booking flows.
 */

/**
 * Availability levels based on capacity percentage thresholds
 */
export type AvailabilityLevel = 'full' | 'high' | 'medium' | 'low' | 'unavailable' | 'closed';

/**
 * Availability indicator result with color and accessibility info
 */
export interface AvailabilityIndicator {
  level: AvailabilityLevel;
  color: string;
  label: string;
  percentage: number;
}

/**
 * Color mapping for each availability level
 * Uses semantic colors that work across light/dark themes
 */
export const AVAILABILITY_COLORS: Record<AvailabilityLevel, string> = {
  full: '#10B981', // Green - 100% available (Tailwind green-500)
  high: '#22C55E', // Light green - 70%+ available (Tailwind green-500)
  medium: '#F59E0B', // Amber - 40-70% available (Tailwind amber-500)
  low: '#EF4444', // Red - 20-40% available (Tailwind red-500)
  unavailable: '#9CA3AF', // Gray - 0-20% or unavailable (Tailwind gray-400)
  closed: '#6B7280', // Dark gray - Venue closed (Tailwind gray-500)
};

/**
 * Calculate availability indicator based on available and total capacity
 *
 * @param available - Number of available slots/capacity
 * @param total - Total slots/capacity
 * @returns AvailabilityIndicator with level, color, label, and percentage
 *
 * @example
 * ```ts
 * const indicator = getAvailabilityIndicator(10, 10);
 * // { level: 'full', color: '#10B981', label: 'Available', percentage: 100 }
 *
 * const indicator2 = getAvailabilityIndicator(5, 10);
 * // { level: 'medium', color: '#F59E0B', label: 'Limited', percentage: 50 }
 * ```
 */
export function getAvailabilityIndicator(available: number, total: number): AvailabilityIndicator {
  // Handle edge cases
  if (total <= 0 || available < 0) {
    return {
      level: 'unavailable',
      color: AVAILABILITY_COLORS.unavailable,
      label: 'Unavailable',
      percentage: 0,
    };
  }

  const percentage = (available / total) * 100;

  // Determine level based on thresholds
  if (percentage === 100) {
    return {
      level: 'full',
      color: AVAILABILITY_COLORS.full,
      label: 'Available',
      percentage,
    };
  } else if (percentage >= 70) {
    return {
      level: 'high',
      color: AVAILABILITY_COLORS.high,
      label: 'Available',
      percentage,
    };
  } else if (percentage >= 40) {
    return {
      level: 'medium',
      color: AVAILABILITY_COLORS.medium,
      label: 'Limited',
      percentage,
    };
  } else if (percentage >= 20) {
    return {
      level: 'low',
      color: AVAILABILITY_COLORS.low,
      label: 'Limited',
      percentage,
    };
  } else if (percentage > 0) {
    return {
      level: 'unavailable',
      color: AVAILABILITY_COLORS.unavailable,
      label: 'Almost Full',
      percentage,
    };
  } else {
    return {
      level: 'unavailable',
      color: AVAILABILITY_COLORS.unavailable,
      label: 'Unavailable',
      percentage: 0,
    };
  }
}
